/**
 * @module domain/audio/utils
 */
import { Map } from 'immutable';
import isNumber from 'lodash/isNumber';
import throttle from 'lodash/throttle';
import { createSelector } from 'reselect';

import * as AudioPlayerEventHandlers from './actions/audioEventHandlers';
import * as AudioConstants from './constants';
import * as D from './deps';
import * as AudioLoader from './libs/AudioPlayback/AudioLoader';
import * as AudioPlayback from './libs/AudioPlayback/AudioPlayback';

const TIME_UPDATE_THROTTLE_MS = 1000;

export { AudioLoader, AudioPlayback };

const providerBoundEvents = {};
const log = D.Logger.child({ component: 'playback/audio/utils' });

export function shouldUseWakeLock(state) {
  return D.Utils.ClientInfo.getBrowserInfo(state).name !== 'Chrome';
}

function getAudioState(state) {
  return state.audio || Map();
}

export function setProviderEvents(url, events) {
  providerBoundEvents[url] = events;
}

export function getProviderEvents(url) {
  const events = providerBoundEvents[url];
  providerBoundEvents[url] = null;
  return events;
}

export function getDefaultAudioErrorHandler(url, token, harnessName) {
  // TODO: get from internal actions
  return AudioPlayerEventHandlers.onError.bind(
    null,
    D.getStore().dispatch,
    url,
    token,
    harnessName,
    {},
  );
}

/**
 * Selector that returns the trackToken of the currently playing audio track
 *
 * @param {object} state - the redux store state
 * @returns {string | null} trackToken
 */
export const getCurrentTrackToken = createSelector(
  state => getAudioState(state),
  audio => {
    const trackToken = audio.get(AudioConstants.AudioPlayerKeys.TRACK_TOKEN);
    return trackToken || null;
  },
);

export function getActivelyPlayingTrack(state) {
  return D.Utils.Core.returnFirstNonNilResult(
    [
      D.Utils.Entity.getTrackById,
      D.getInDomain('playback.selectors.getCurrentItemAudioTrack', null),
    ],
    state,
    getCurrentTrackToken(state),
  );
}

/**
 * Selector that returns the CURRENT_TIME of the currently playing track (if there is one)
 * and 0 otherwise.
 *
 * @param {object} state - the redux store state
 * @returns {number} elapsed playback time
 */
export const getCurrentTrackTime = createSelector(
  state => getAudioState(state),
  audio => {
    const time = audio.get(AudioConstants.AudioPlayerKeys.CURRENT_TIME);
    return !isNaN(time) ? time : 0;
  },
);

/**
 * Selector that returns the duration of the currently playing track (if there is one)
 * and -1 if not present
 *
 * @param {object} state - the redux store state
 * @returns {number} duration
 */
export const getCurrentTrackDuration = createSelector(
  state => getAudioState(state),
  audio => {
    const duration = audio.get(AudioConstants.AudioPlayerKeys.DURATION);
    return !isNaN(duration) ? duration : -1;
  },
);

/**
 * Selector that returns the current timestamp
 *
 * @param {object} state - the redux store state
 * @returns {number} timestamp in milliseconds
 */
export const getCurrentTimestamp = createSelector(
  state => getAudioState(state),
  audio => {
    return audio.get(AudioConstants.AudioPlayerKeys.CURRENT_TIMESTAMP) || 0;
  },
);

/**
 * Function retrieves the state of the audio playing...
 *
 * @param {object} state - the redux store state
 * @returns {boolean | null} is Audio playing?
 */
export function isAudioPlayerPlaying(state) {
  return getAudioState(state).get(AudioConstants.AudioPlayerKeys.PLAYING) || false;
}

export function getGainFromTrack(track) {
  const trackGain = track ? parseFloat(track.get('fileGain')) : 0;
  if (isNumber(trackGain) && !isNaN(trackGain)) {
    return trackGain;
  }
  return 0;
}

export function getGain(state) {
  const track = getActivelyPlayingTrack(state);
  return getGainFromTrack(track);
}

export function getActiveTrackUrl(state) {
  return getAudioState(state).get(AudioConstants.AudioPlayerKeys.ACTIVE_TRACK_URL);
}

export function isExplicitlyPaused(state) {
  return getAudioState(state).get(AudioConstants.AudioPlayerKeys.EXPLICITLY_PAUSED);
}

/**
 * Function that takes TimeRanges and returns how much is buffered
 *
 * @param {TimeRanges|number} buffered - TimeRanges object indicating the buffered time ranges or a number indicating the amount of audio buffered in seconds
 * @returns {number} total amount buffered
 */
export function getBuffered(buffered) {
  if (isNumber(buffered)) {
    return buffered;
  }

  let tot = 0;
  if (buffered.length > 0) {
    for (let i = 0; i < buffered.length; ++i) {
      tot = Math.max(tot, buffered.end(i));
    }
  }
  return tot;
}

/*
 * Returns the current buffered amount
 */
export function getCurrentBufferedAmount(state) {
  return getAudioState(state).get(AudioConstants.AudioPlayerKeys.BUFFERED);
}

export function getBufferedRemaining(state) {
  return getAudioState(state).get(AudioConstants.AudioPlayerKeys.BUFFERED_REMAINING);
}

/**
 * getNumberOfBandwidthErrors - returns number of consecutive bandwidth errors we've accrued
 *
 * @param  {object} state - application state
 * @returns {number}
 */
export function getNumberOfBandwidthErrors(state) {
  return getAudioState(state).getIn(
    [AudioConstants.AudioPlayerKeys.ERROR_COUNTS, AudioConstants.AudioPlayerKeys.BANDWIDTH_ERRORS],
    -1,
  );
}

/**
 * getHasExperiencedBandwidthError - gets whether the user has experienced a bandwidth error in this session
 *
 * @param  {object} state - application state
 * @returns {boolean}
 */
export function getHasExperiencedBandwidthError(state) {
  return getAudioState(state).get(AudioConstants.AudioPlayerKeys.HAS_EXPERIENCED_BANDWIDTH_ERROR);
}

/**
 * getNumberOfSrcNotSupportedErrors - returns number of consecutive bandwidth errors we've accrued
 *
 * @param  {object} state - application state
 * @returns {number}
 */
export function getNumberOfSrcNotSupportedErrors(state) {
  return getAudioState(state).getIn(
    [
      AudioConstants.AudioPlayerKeys.ERROR_COUNTS,
      AudioConstants.CommonAudioErrorCodes.SRC_NOT_SUPPORTED,
    ],
    -1,
  );
}

export function getNumberOfDecodeErrors(state) {
  return getAudioState(state).getIn(
    [AudioConstants.AudioPlayerKeys.ERROR_COUNTS, AudioConstants.CommonAudioErrorCodes.DECODE],
    -1,
  );
}

export function getNumberOfNetworkErrors(state) {
  return getAudioState(state).getIn(
    [AudioConstants.AudioPlayerKeys.ERROR_COUNTS, AudioConstants.CommonAudioErrorCodes.NETWORK],
    -1,
  );
}

/*
 * A selector for getting whether we should use the wake lock:
 * a muted looping video that keeps the computer from going to sleep
 */
export function getWakeLockStatus(state) {
  return getAudioState(state).get(AudioConstants.AudioPlayerKeys.HAS_WAKE_LOCK);
}

/*
 * A selector for getting whether there's something other than our core audio
 * playback that is using the AudioPlayback library. An example would be the
 * sample player.
 */
export function isAuxPlaybackPlaying(state) {
  return getAudioState(state).get(AudioConstants.AudioPlayerKeys.AUX_PLAYBACK_PLAYING);
}

export function getAuxSourceUrl(state) {
  return getAudioState(state).get(AudioConstants.AudioPlayerKeys.AUX_PLAYBACK_URL);
}

export function isInitialized(state) {
  return getAudioState(state).get(AudioConstants.AudioPlayerKeys.INIT);
}

/*
 * Determine which functions a provider's events should bind to based on
 * the audio type (one of AudioTypes constants).
 * The url is required if it is a sample
 */
export function assignProviderEvents(
  audioType,
  dispatch,
  { url = null, token = null, harnessName = null },
) {
  switch (audioType) {
    case AudioConstants.AudioTypes.AD:
    case AudioConstants.AudioTypes.TRACK:
    case AudioConstants.AudioTypes.PODCAST:
    case AudioConstants.AudioTypes.VOICETRACK:
      return {
        timeupdate: throttle(
          AudioPlayerEventHandlers.onTimeUpdate.bind(null, dispatch, url, token),
          TIME_UPDATE_THROTTLE_MS,
        ),
        durationchange: AudioPlayerEventHandlers.onDurationChange.bind(null, dispatch, url, token),
        playing: AudioPlayerEventHandlers.onPlaying.bind(null, dispatch, url, token),
        error: AudioPlayerEventHandlers.onError.bind(null, dispatch, url, token, harnessName),
        pause: AudioPlayerEventHandlers.onPaused.bind(null, dispatch, url, token),
        ended: AudioPlayerEventHandlers.onEnded.bind(null, dispatch, url, token, harnessName),
        canplaythrough: AudioPlayerEventHandlers.onCanPlayThrough.bind(null, dispatch, url, token),
        playbackMetrics: AudioPlayerEventHandlers.onPlaybackMetrics.bind(
          null,
          dispatch,
          url,
          token,
        ),
      };
    case AudioConstants.AudioTypes.SAMPLE:
      return {
        playing: AudioPlayerEventHandlers.onSamplePlaying.bind(null, dispatch, url),
        timeupdate: throttle(
          AudioPlayerEventHandlers.onTimeUpdate.bind(null, dispatch, url, token),
          TIME_UPDATE_THROTTLE_MS,
        ),
        // No such thing as pausing samples, but need event tied with clearProvider
        pause: AudioPlayerEventHandlers.onSamplePaused.bind(null, dispatch),
        ended: AudioPlayerEventHandlers.onSampleEnded.bind(null, dispatch),
      };
  }
  return null;
}

export function hasAudioInitCountExceededMax(state) {
  return (
    getAudioState(state).get(AudioConstants.AudioPlayerKeys.INIT_AUDIO_RETRIES) >
    AudioConstants.MAX_AUDIO_RETRIES
  );
}

export function hasAudioRecoverableErrorCountExceededMax(state) {
  const notSupportedSrcErrorCount = getNumberOfSrcNotSupportedErrors(state);
  const decodeErrorCount = getNumberOfDecodeErrors(state);
  const networkErrorCount = getNumberOfNetworkErrors(state);

  return (
    Math.max(notSupportedSrcErrorCount, decodeErrorCount, networkErrorCount) >
    AudioConstants.MAX_AUDIO_RETRIES
  );
}

function isAudioAd(track) {
  return Map.isMap(track) && track.get('trackType') === 'AUDIO_AD';
}

function isPodcast(track) {
  const type = Map.isMap(track) && track.get('type');
  return type && type === D.Constants.TrackType.TrackType.PODCAST_EPISODE;
}

function isVoiceTrack(track) {
  return Map.isMap(track) && track.get('type') === D.Constants.TrackType.TrackType.VOICE_TRACK;
}

export function getAudioType(track) {
  if (isAudioAd(track)) {
    return AudioConstants.AudioTypes.AD;
  }

  if (isPodcast(track)) {
    return AudioConstants.AudioTypes.PODCAST;
  }

  if (isVoiceTrack(track)) {
    return AudioConstants.AudioTypes.VOICETRACK;
  }

  return AudioConstants.AudioTypes.TRACK;
}

export function getAudioEncoding(track) {
  const encoding = track.get('encoding');
  if (encoding) {
    // Common formats: mp3, mp4, m4a, and aacplus
    // - podcast = 128k mp3
    // - track for on-demand or high-quality streaming = 192k aac+
    // - everything else = 64k aac+
    // Note:
    // 1. For station, we get the `audioEncoding` value (mp3) directly from the legacy API.
    // 2. aacplus is sent in mp4 container.
    return encoding;
  }

  log.info('No audio encoding value', track);
  return track.get('key')
    ? AudioConstants.AudioEncodingFormats.MP4
    : AudioConstants.AudioEncodingFormats.MP3;
}

export function getAudioSrcObj(track) {
  return (
    track && {
      url: track.get('audioURL'),
      format: track.get('audioEncoding'),
      token: track.get('trackToken'),
      track, // @todo - WEBCLIENT-2734 - move towards just using `track` and no longer needing to use `token`
      audioType: getAudioType(track),
      key: track.get('key'),
    }
  );
}

function getTrackTypeForLogging(track) {
  let trackType;
  const audioType = getAudioType(track);
  if (audioType === AudioConstants.AudioTypes.TRACK) {
    const stationId = track.get('stationId');
    if (stationId) {
      trackType = 'STATION';
    } else {
      trackType = 'ONDEMAND';
    }
  } else {
    trackType = audioType;
  }
  return trackType;
}

export function getTrackDataForLogging(state) {
  const track = getActivelyPlayingTrack(state);
  const audioUrl = track.get('audioURL');
  const trackData = {
    audioUrl,
    isEncrypted: D.Utils.AudioPlayerUtils.isTrackEncrypted(track),
    contentHost: D.Utils.AudioPlayerUtils.getAudioContentHost(audioUrl),
    trackId: track.get('sourceId') || track.get('musicId'),
    trackType: getTrackTypeForLogging(track),
    duration: track.get('duration') || track.get('trackLength'),
    currentTime: getCurrentTrackTime(state),
    bufferedAmount: getCurrentBufferedAmount(state),
    bufferedRemaining: getBufferedRemaining(state),
  };

  return trackData;
}

export function shouldShowAudioLoading(state) {
  const activeTrackUrl = getActiveTrackUrl(state);
  const audioPlayerPlaying = isAudioPlayerPlaying(state);
  const explicitlyPaused = isExplicitlyPaused(state);
  return activeTrackUrl && !audioPlayerPlaying && !explicitlyPaused;
}

export const externalUtils = {
  isAudioPlayerPlaying,
  getActivelyPlayingTrack,
  getCurrentTrackToken,
  getCurrentTrackTime,
  getCurrentTimestamp,
  getCurrentTrackDuration,
  getHasExperiencedBandwidthError,
  isExplicitlyPaused,
  getAudioType,
};
